import { useCallback, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";

import {
  ComplianceService,
  type FrameworkCode,
  type SubscriptionListResponse,
} from "@/api";
import { useApi, useMutation } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { formatFrameworkCode } from "@/lib/compliance-helpers";
import { formatDateID } from "@/lib/format";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ComplianceSubscribeDialog } from "./compliance-subscribe-dialog";

const TENANT_ADMIN_ROLES = new Set(["admin", "super_admin"]);

export function ComplianceFrameworksTab() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const canManage = user ? TENANT_ADMIN_ROLES.has(user.role) : false;
  const [subscribeOpen, setSubscribeOpen] = useState(false);

  const fetcher = useCallback(
    () => ComplianceService.listSubscriptions({ active_only: false }),
    []
  );
  const { data, error, isLoading, refetch } =
    useApi<SubscriptionListResponse>(fetcher);

  const unsubscribe = useMutation<FrameworkCode, void>(
    (code) => ComplianceService.unsubscribe(code),
    { onSuccess: () => void refetch() }
  );

  const items = useMemo(() => data?.items ?? [], [data]);
  const subscribedCodes = useMemo(
    () => items.filter((s) => s.is_active).map((s) => s.framework_code),
    [items]
  );

  return (
    <div className="space-y-4">
      {error && <Alert variant="warning">{parseApiError(error)}</Alert>}
      {unsubscribe.error && (
        <Alert variant="danger">{parseApiError(unsubscribe.error)}</Alert>
      )}

      <div className="flex flex-col gap-3 tablet:flex-row tablet:items-center tablet:justify-between">
        <p className="text-sm text-neutral-600">
          {t("compliance.frameworks.intro")}
        </p>
        {canManage && (
          <Button onClick={() => setSubscribeOpen(true)} size="sm">
            <Plus size={14} />
            {t("compliance.actions.subscribe")}
          </Button>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("compliance.frameworks.colFramework")}</TableHead>
              <TableHead>{t("compliance.frameworks.colStatus")}</TableHead>
              <TableHead className="hidden tablet:table-cell">
                {t("compliance.frameworks.colTargetCertification")}
              </TableHead>
              <TableHead className="hidden tablet:table-cell">
                {t("compliance.frameworks.colSince")}
              </TableHead>
              <TableHead className="text-right">
                {t("compliance.frameworks.colActions")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 && (
              <>
                {[0, 1, 2].map((i) => (
                  <TableRow key={`fw-sk-${i}`}>
                    <TableCell colSpan={5} className="py-4">
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
            {!isLoading && items.length === 0 && (
              <TableEmpty
                colSpan={5}
                message={t("compliance.frameworks.empty")}
              />
            )}
            {items.map((sub) => {
              const target = (sub.subscription_metadata?.[
                "target_certification_date"
              ] ?? null) as string | null;
              return (
                <TableRow key={sub.id}>
                  <TableCell className="font-medium text-ocean-900">
                    {formatFrameworkCode(sub.framework_code)}
                  </TableCell>
                  <TableCell>
                    {sub.is_active ? (
                      <Badge tone="success">
                        {t("compliance.frameworks.statusActive")}
                      </Badge>
                    ) : (
                      <Badge tone="neutral">
                        {t("compliance.frameworks.statusInactive")}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="hidden tablet:table-cell text-neutral-600">
                    {sub.target_certification_date
                      ? formatDateID(sub.target_certification_date)
                      : target
                        ? formatDateID(target)
                        : "—"}
                  </TableCell>
                  <TableCell className="hidden tablet:table-cell text-neutral-600">
                    {formatDateID(sub.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    {canManage && sub.is_active && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={unsubscribe.isLoading}
                        onClick={() =>
                          void unsubscribe.mutate(
                            sub.framework_code as FrameworkCode
                          )
                        }
                      >
                        <Trash2 size={14} />
                        {t("compliance.actions.unsubscribe")}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <ComplianceSubscribeDialog
        open={subscribeOpen}
        onClose={() => setSubscribeOpen(false)}
        onSuccess={() => {
          setSubscribeOpen(false);
          void refetch();
        }}
        excludeCodes={subscribedCodes}
      />
    </div>
  );
}
